// A built body's 3D model as data (docs/claude/tilt.md, *Parts with a 3D
// model*): a duct fan, a steam conveyor, a steam cube, a hot tub and an essence
// doser, each generated from the part's own figures as the `PartModel3d` a
// stored model is — its solids by role, the roles' colours and finishes —, so
// ONE builder (shared/partSolids.ts) makes every view of it: the 3D view's
// meshes (three/scene3d.ts), and the pieces a section cuts and a tilted plan
// shows (partModel.ts). Pure and DOM-free (test/builtModels.test.mjs).

import type {
  PartModel3d,
  PartSolid3d,
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  type DuctFanShape,
  ductFanSolids,
} from '../shared/ductFans';
import {
  CONVEYOR_COPPER,
  conveyorSolids,
} from '../shared/steamConveyors';
import { PRODUCT_COLOR } from '../shared/partSolids';
import {
  DOSER_COLOURS,
  type DoserFigure,
} from '../shared/doserSolids';
import {
  objectHeight,
  partHeight,
} from './heights';
import { hotTubLayout } from './hotTub';
import { type ProjectedBody } from './partLook';

/** A steam cube's figures: a stone block with walls `wall` thick round a recess
 *  `recess` deep from its top, the recess floored with a steel grille `grille`
 *  thick — its plan (render.ts) and its model alike. */
export const STEAM_CUBE = { wall: 50, recess: 200, grille: 3, stone: '#3c3d42', steel: '#80858e' } as const;

/** A duct fan's open ends. */
const FAN_HOLE = '#2b2f33';
/** A steam conveyor's steel, when the copy has no colour of its own. */
const CONVEYOR_STEEL = '#b8bec4';
/** A hot tub's moulded acrylic, its mosaic coping, its water. */
const HOT_TUB = { acrylic: '#f4f6f7', mosaic: '#8e979c', water: '#4fc3da' } as const;

const prism = (role: string, ring: Vec2[], z0: number, z1: number, holes: Vec2[][] = []): PartSolid3d =>
  (holes.length ? { kind: 'prism', role, ring, holes, z0, z1 } : { kind: 'prism', role, ring, z0, z1 });
const rect = (x0: number, y0: number, x1: number, y1: number): Vec2[] => [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];

/** A duct fan from its datasheet's figures (`ductFanSolids`): its open ends dark
 *  discs (listed first: the one facing away stays under its body), its body
 *  revolved about the duct's axis and its boxes, in the copy's colour. */
function fanModel(inst: ProductInstance, f: DuctFanShape): PartModel3d {
  const s = ductFanSolids(f), at: [number, number] = [s.axis.y, s.axis.z];
  const first = s.stations[0], last = s.stations[s.stations.length - 1];
  return {
    size: [inst.width, inst.depth, f.height],
    colours: { body: PRODUCT_COLOR, inlet: FAN_HOLE, outlet: FAN_HOLE },
    solids: [
      { kind: 'revolve', role: 'inlet', axis: 'x', at, stations: [[first[0], 0], [first[0], first[1]]] },
      { kind: 'revolve', role: 'outlet', axis: 'x', at, stations: [[last[0], last[1]], [last[0], 0]] },
      { kind: 'revolve', role: 'body', axis: 'x', at, stations: s.stations },
      ...s.boxes.map((b) => prism('body', b.ring, b.z0, b.z1)),
    ],
  };
}

/** A steam conveyor on Stenal's figures (`conveyorSolids`) through a wall `room`
 *  deep: its steel (the copy's colour) and its copper pipe, both metal. */
function conveyorModel(inst: ProductInstance, room: number): PartModel3d {
  return {
    size: [inst.width, inst.depth, partHeight(inst)],
    colours: { body: CONVEYOR_STEEL, copper: CONVEYOR_COPPER },
    metal: ['body', 'copper'],
    solids: conveyorSolids(room).map((s) => prism(s.copper ? 'copper' : 'body', s.ring, s.z0, s.z1)),
  };
}

/** A steam cube (`STEAM_CUBE`): its stone (the copy's colour) — the base under
 *  the recess, the walls round it — and the steel grille on the recess's floor;
 *  a block too small for a recess, its stone whole. */
function steamCubeModel(inst: ProductInstance): PartModel3d {
  const H = objectHeight(inst), hw = inst.width / 2, hd = inst.depth / 2, w = STEAM_CUBE.wall;
  const outer = rect(-hw, -hd, hw, hd), inner = rect(-hw + w, -hd + w, hw - w, hd - w);
  const floor = Math.max(0, H - STEAM_CUBE.recess);
  const recessed = hw - w > 0.5 && hd - w > 0.5;
  return {
    size: [inst.width, inst.depth, H],
    colours: { body: STEAM_CUBE.stone, grille: STEAM_CUBE.steel },
    metal: ['grille'],
    solids: recessed
      ? [prism('body', outer, 0, floor), prism('body', outer, floor, H, [inner]), prism('grille', inner, floor, floor + STEAM_CUBE.grille)]
      : [prism('body', outer, 0, H)],
  };
}

/** A hot tub from `hotTubLayout` (the plan's layout too): its cabinet in the
 *  copy's colour under the acrylic deck cap, the coping (mosaic tile, or the
 *  moulded acrylic rim), the shell (floor, the ledge round the footwell, the
 *  seats, the lounger's steps, the platforms) and the water from a millimetre
 *  over the floor — coplanar with it, it would z-fight — to the waterline. */
function hotTubModel(inst: ProductInstance): PartModel3d {
  const L = hotTubLayout(inst.width, inst.depth, objectHeight(inst), inst.hotTubModel);
  const mosaic = L.coping === 'mosaic';
  return {
    size: [inst.width, inst.depth, objectHeight(inst)],
    colours: { body: PRODUCT_COLOR, deck: HOT_TUB.acrylic, coping: mosaic ? HOT_TUB.mosaic : HOT_TUB.acrylic, shell: HOT_TUB.acrylic, water: HOT_TUB.water },
    acrylic: mosaic ? ['deck', 'shell'] : ['coping', 'deck', 'shell'],
    water: ['water'],
    solids: [
      prism('body', L.outline, 0, L.z.cabinet, [L.rim]),
      prism('deck', L.outline, L.z.cabinet, L.z.deck, [L.rim]),
      prism('coping', L.rim, 0, L.z.rim, [L.shell]),
      prism('shell', L.shell, 0, L.z.floor),
      prism('shell', L.shell, 0, L.z.ledge, [L.footwell]),
      ...[...L.seats, ...(L.lounger?.steps ?? []), ...L.platforms].map((s) => prism('shell', s.poly, 0, s.top)),
      prism('water', L.water, L.z.floor + 1, L.z.water),
    ],
  };
}

/** An essence dosing product on its figures (shared/doserSolids.ts): its board
 *  or shelf the `body` — the copy's colour, glossy acrylic —, each other role
 *  in its own colour. */
function doserModel(f: DoserFigure): PartModel3d {
  const { board, ...roles } = DOSER_COLOURS;
  return { size: [f.width, f.depth, f.height], colours: { body: board, ...roles }, acrylic: ['body'], solids: f.solids };
}

type BuiltBody = Exclude<ProjectedBody, { kind: 'stored' }>;

/** Each built body's generator — a Record, so a body partLook projects does not
 *  compile until it says how its model is made. */
const BUILT: { [B in BuiltBody as B['kind']]: (inst: ProductInstance, body: B) => PartModel3d } = {
  conveyor: (inst, b) => conveyorModel(inst, b.throughWall),
  doser: (_inst, b) => doserModel(b.figure),
  fan: (inst, b) => fanModel(inst, b.shape),
  hotTub: (inst) => hotTubModel(inst),
  steamCube: (inst) => steamCubeModel(inst),
};

/** A projected body's model (partLook.ts `isProjected`): a stored one as it is,
 *  a built one generated from the part's figures. */
export function bodyModel(inst: ProductInstance, body: ProjectedBody): PartModel3d {
  if (body.kind === 'stored') return body.model;
  return (BUILT[body.kind] as (inst: ProductInstance, body: BuiltBody) => PartModel3d)(inst, body);
}
